import EzController from 'controllers/ez-base-controller';
import Cookies from 'js-cookie';
import { getAjaxModal, insertAjaxContent } from 'lib/ajax-helpers';
import { ezConfirm } from 'lib/modal_helpers';
import { triggerCustomEvent, featureEnabled } from 'lib/helpers';
import {
  sendTrackingEvent,
  findCategoryByElement,
  trackCompleteEvent,
  NEW_ANALYTICS_FEATURE,
} from 'components/analytics/conversions/tracking';
import { JOIN_PRO_TRIGGERED, JOIN_PRO_COMPLETE } from 'components/analytics/conversions/constants/actions';
import events from 'lib/events';
import { addSessionDownloadActivity } from 'lib/sessions-manager';

export default class DownloadButtonPricingController extends EzController {
  static targets = ['downloadButton']

  initialize() {
    if (document.body.dataset.userSignedIn === 'true') {
      this.disableDownloadLimitModal();
    } else if (document.body.dataset.downloadLimitModalTest === 'true') {
      // split test: download_limit_modal
      this.disableDownloadLimitModal();
    }

    window.addEventListener('DownloadLimitModalSeen', this.disableDownloadLimitModal);
  }

  connect() {
    if (!this.isTurbolinksPreview) {
      const searchData = window.localStorage.getItem(`resource-${this.resourceId}`);
      if (searchData) {
        this.searchData = JSON.parse(searchData);
        window.localStorage.removeItem(`resource-${this.resourceId}`);
      }
      this.flagLoaded();
    }
  }

  disconnect() {
    window.removeEventListener('DownloadLimitModalSeen', this.disableDownloadLimitModal);
  }

  freeDownload = (event) => {
    event.preventDefault();

    if (this.hasHitDownloadLimit() && !window.disableDownloadLimitModal) {
      this.openDownloadLimitModal();
    } else if (this.downloadButtonTarget.dataset.dlAdModalPath) {
      this.showDownloadAdPopup();

      if (this.downloadButtonTarget.getAttribute('data-license-type') !== 'editorial') {
        this.showFreeAttributionSlidedown();
      }
    } else {
      this.triggerDownload();
    }
  }

  showFreeAttributionSlidedown() {
    // first, get rid of any existing slidedowns
    if (document.querySelector('[data-controller="free-attribution-slidedown"]')) {
      const existingSlidedown = document.querySelector('[data-controller="free-attribution-slidedown"]');
      existingSlidedown.remove();
    }

    const resourceId = document.querySelector('[data-controller*="resource-show-main"]').getAttribute('data-resource-id');

    const slidedownParent = document.querySelector('.ez-page-wrapper__content');

    insertAjaxContent(`/resources/${resourceId}/show_free_attr_slidedown`,
      slidedownParent,
      'afterbegin',
      () => {
        triggerCustomEvent(events.show_page.reveal_free_attribution_slidedown);
      });
  }

  disableDownloadLimitModal = () => {
    window.disableDownloadLimitModal = true;
  }

  get curPopCount() {
    return parseInt(Cookies.get('pop_count'), 10) || 0;
  }

  set curPopCount(value) {
    Cookies.set('pop_count', value);
  }

  hasHitDownloadLimit() {
    return this.curPopCount >= 3;
  }

  triggerDownload = (event, reloadPage = false) => {
    addSessionDownloadActivity(this.resourceId);
    const downloadFileUrl = (event && (typeof event.detail.downloadFileUrl !== 'undefined')) ? event.detail.downloadFileUrl : this.downloadButtonTarget.getAttribute('href');

    if (!window.disableDownloadLimitModal) this.curPopCount += 1;

    if (reloadPage) {
      window.open(downloadFileUrl, '_blank');
      triggerCustomEvent(events.show_page.file_delivered);

      window.setTimeout(() => {
        window.location.reload();
      }, 1000);
    } else {
      triggerCustomEvent(events.show_page.file_delivered);
      window.location.href = downloadFileUrl;
    }
  }

  showDownloadAdPopup() {
    const downloadAdModalPath = this.downloadButtonTarget.dataset.dlAdModalPath;

    this.downloadButtonTarget.setAttribute('remote', 'true');

    window.addEventListener('TriggerDownload', this.triggerDownload, { once: true });

    getAjaxModal(downloadAdModalPath);
  }

  openDownloadLimitModal() {
    const downloadLimitModalPath = this.downloadButtonTarget.dataset.dlLimitModalPath;

    this.downloadButtonTarget.setAttribute('remote', 'true');

    getAjaxModal(downloadLimitModalPath);
  }

  spendCreditAndDownload = (event) => {
    // Need to reload the page so a new download button gets rendered
    this.triggerDownload(event, true);

    this.creditSpent = true;
  }

  confirmCreditSpend(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    if (this.creditSpent) {
      this.triggerDownload();
    } else {
      ezConfirm(
        event.currentTarget.getAttribute('data-confirm-text'),
        this.spendCreditAndDownload,
        ['Okay', 'Cancel'],
      );
    }
  }

  sendDownloadEvent() {
    let eventDetails = {
      resourceId: this.resourceId,
      resourceType: this.resourceType,
    };

    if (this.searchData) {
      eventDetails = Object.assign(eventDetails, this.searchData);
    }

    triggerCustomEvent(events.download_btn.initial_click, eventDetails);
  }

  joinProClick(event) {
    this.sendDownloadEvent();

    if (!featureEnabled(NEW_ANALYTICS_FEATURE)) return;

    const category = findCategoryByElement(event.target);

    sendTrackingEvent({
      element: event.target,
      data: {
        action: JOIN_PRO_TRIGGERED,
        category,
        label: 'Pro',
      },
    });

    trackCompleteEvent({ action: JOIN_PRO_COMPLETE, category });
  }

  sendDownloadEventAndRedirect(event) {
    event.preventDefault();
    this.sendDownloadEvent();

    window.location.assign(this.downloadButtonTarget.href);
  }

  handleOptionClick = (event) => {
    event.preventDefault();
    const option = event.target.closest('.download-btn__option');
    if (option.classList.contains('is-selected')) return false;

    this.downloadButtonTarget.textContent = option.dataset.dlBtnText;
    this.downloadButtonTarget.dataset.action = option.dataset.buttonActions || '';
    this.downloadButtonTarget.href = option.dataset.buttonHref || '#';

    this.downloadButtonTarget.classList.remove('has-download-icon');
    if (option.dataset.hasDownloadIcon) this.downloadButtonTarget.classList.add('has-download-icon');

    if (option.dataset.buttonModal) {
      this.downloadButtonTarget.dataset.dlAdModalPath = option.dataset.buttonModal;
    } else {
      this.downloadButtonTarget.removeAttribute('data-dl-ad-modal-path');
    }

    if (option.dataset.confirmText) {
      this.downloadButtonTarget.dataset.confirmText = option.dataset.confirmText;
    } else {
      this.downloadButtonTarget.removeAttribute('data-confirm-text');
    }

    if (option.classList.contains('has-multiple-options')) {
      const checkedSubOption = option.querySelector('input[type=radio]:checked');
      const subOption = checkedSubOption.closest('.ez-input');

      this.setSubOption(subOption, option);
    }

    this.setIsSelected(option);
    return true;
  }

  setIsSelected = (option) => {
    const selectedOptions = this.element.querySelectorAll('.is-selected');

    selectedOptions.forEach((selectedOption) => {
      selectedOption.classList.remove('is-selected');
      const selectedTitleRadio = selectedOption.querySelector('.download-btn__option__title input[type=radio]');
      selectedTitleRadio.checked = false;
    });

    option.classList.add('is-selected');
    const titleRadio = option.querySelector('.download-btn__option__title input[type=radio]');
    titleRadio.checked = true;
  }

  handleSubPlanOptionClick = (event) => {
    event.preventDefault();

    const closedPrice = this.element.querySelector(
      '.download-btn__option--subs .download-btn__option__title .download-btn__option__price',
    );

    const subOption = event.target.closest('.ez-input');

    const subOptionPrice = subOption.querySelector('.download-btn__option__price').innerHTML;
    closedPrice.innerHTML = subOptionPrice;

    const subsWrapper = event.target.closest('.download-btn__option__multiple');
    this.setSubOption(subOption, subsWrapper);
  }

  setSubOption = (subOption, subsWrapper) => {
    const checkedSubOption = subsWrapper.querySelector('input[type=radio]:checked');
    if (checkedSubOption) checkedSubOption.checked = false;

    this.downloadButtonTarget.href = subOption.dataset.buttonHref || '#';
    subOption.querySelector('input[type=radio]').checked = true;
  }

  get isTurbolinksPreview() {
    return document.documentElement.hasAttribute('data-turbolinks-preview');
  }

  get resourceId() {
    return document.querySelector('.ez-resource-show').dataset.resourceId;
  }

  get resourceType() {
    return document.querySelector('.ez-resource-show').dataset.resourceType;
  }
}
